/** The Network section's layout route (#310): the page header above tabs,
 *  with the active tab's content in the <Outlet/>.
 *
 *  Tabs are PATHS (a tab is a place — switching pushes history); filters are
 *  query params owned by each tab (a filter refines a place — they replace).
 *  The bare /network path IS the Connect tab via an index child route: no
 *  redirect, no duplicate URL for the same screen. */
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Group, Stack, Tabs, Text, Title } from "@mantine/core";
import { useNetworkStatus } from "@/context/network-context";
import { SectionGuides } from "@/components/guides/section-guides";

const TABS = [
  { value: "connect", label: "Connect", path: "/network" },
  { value: "devices", label: "Devices", path: "/network/devices" },
  { value: "performance", label: "Performance", path: "/network/performance" },
] as const;

export default function NetworkLayout() {
  const networkStatus = useNetworkStatus();
  const location = useLocation();
  const navigate = useNavigate();

  const active =
    TABS.find((t) => t.value !== "connect" && location.pathname.startsWith(t.path))?.value ?? "connect";

  return (
    <Stack p="md" gap="lg">
      <Group justify="space-between">
        <Title order={2}>Network</Title>
        {networkStatus.internalIp && (
          <Group gap="xs">
            <Box w={8} h={8} style={{ borderRadius: "50%", background: "var(--mantine-color-green-5)" }} />
            <Text fz="sm" c="dimmed">Your IP: <Text fz="sm" ff="monospace" component="span">{networkStatus.internalIp}</Text></Text>
          </Group>
        )}
        {!networkStatus.internalIp && networkStatus.status === "external" && (
          <Group gap="xs">
            <Box w={8} h={8} style={{ borderRadius: "50%", background: "var(--mantine-color-yellow-5)" }} />
            <Text fz="sm" c="dimmed">Off-site</Text>
          </Group>
        )}
      </Group>
      <SectionGuides area="network" />

      <Tabs
        value={active}
        onChange={(value) => {
          const tab = TABS.find((t) => t.value === value);
          // A tab switch is navigation to a place — push, so Back returns to
          // the previous tab.
          if (tab) navigate(tab.path);
        }}
      >
        <Tabs.List>
          {TABS.map((t) => (
            <Tabs.Tab key={t.value} value={t.value}>
              {t.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <Outlet />
    </Stack>
  );
}
